/**
 * Which guided questions a Guided Mode session shows, and where it starts.
 *
 * Guided Mode is a wizard for what is still open, so answered questions are
 * left out, except the one the caller asked to edit (`focusQuestionId`): that
 * question stays in, with its existing answer, and the session starts on it.
 * AI skip decisions never remove the focused question either.
 */
export interface GuidedAnswerRecord { answer?: unknown }

export function isAnswered(rec: GuidedAnswerRecord | undefined | null): boolean {
  if (!rec) return false;
  const a = rec.answer;
  if (a === null || a === undefined || a === '') return false;
  if (Array.isArray(a) && a.length === 0) return false;
  return true;
}

export function selectGuidedQuestions<Q extends { id: string }>(
  generated: Q[],
  answers: Record<string, GuidedAnswerRecord> | null | undefined,
  opts: { focusQuestionId?: string | null; skippedIds?: Iterable<string> } = {},
): { questions: Q[]; startIndex: number; answeredCount: number } {
  const skipped = new Set(opts.skippedIds ?? []);
  const focus = opts.focusQuestionId ?? null;
  let answeredCount = 0;
  const questions = generated.filter((q) => {
    const answered = isAnswered(answers?.[q.id]);
    if (answered) answeredCount++;
    if (q.id === focus) return true;
    if (answered) return false;
    return !skipped.has(q.id);
  });
  const startIndex = focus ? Math.max(0, questions.findIndex((q) => q.id === focus)) : 0;
  return { questions, startIndex, answeredCount };
}
